/**
 * Light or dark, and who decided.
 *
 * Three states are one too many to put in front of anyone, so the control is a
 * switch and the third state is unwritten: until someone touches it, nothing
 * is stored and the operating system is followed live — change the Mac to
 * light at sunset and this follows. The first press pins a choice, and from
 * then on this device is whatever they picked, whatever the OS does.
 *
 * The attribute is set on <html> here and again by an inline script in
 * index.html, which runs before the first paint. Without that script the app
 * would flash the dark default on the way to a light theme.
 */
import { useCallback, useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';
const KEY = 'tvx.theme';

const mq = () => window.matchMedia('(prefers-color-scheme: light)');

export function systemTheme(): Theme {
  try { return mq().matches ? 'light' : 'dark'; } catch { return 'dark'; }
}

/** The pinned choice, or null while the OS is still in charge. */
export function storedTheme(): Theme | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch { return null; }
}

export function applyTheme(t: Theme) {
  document.documentElement.dataset.theme = t;
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, set] = useState<Theme>(() => storedTheme() ?? systemTheme());

  useEffect(() => { applyTheme(theme); }, [theme]);

  /* Only while nothing is pinned: a stored choice outranks the OS. */
  useEffect(() => {
    if (storedTheme()) return;
    const m = mq();
    const follow = () => { if (!storedTheme()) set(systemTheme()); };
    m.addEventListener('change', follow);
    return () => m.removeEventListener('change', follow);
  }, [theme]);

  const choose = useCallback((t: Theme) => {
    try { localStorage.setItem(KEY, t); } catch { /* private mode */ }
    set(t);
  }, []);

  return [theme, choose];
}
